import { Card, Tooltip } from '@app/features/analytics/components';
import { ErrorScreen } from '@app/features/analytics/components/error-states/error-screen';
import { NoFilterResults } from '@app/features/analytics/components/error-states/no-filter-results';
import { NoResults } from '@app/features/analytics/components/error-states/no-results';
import { useAnalytics } from '@app/features/analytics/hooks/use-analytics';
import { useFetch } from '@app/features/analytics/hooks/use-fetch';
import { useAnalyticsContext } from '@app/features/analytics/providers/analytics-provider';
import { fetchMostViewedHubVideos } from '@app/features/analytics/services/endpoints';
import { WatchedVideo } from '@app/features/analytics/types';
import {
  formatDuration,
  transformToCamelCase,
} from '@app/features/analytics/utils';
import { useReadDashboardSharedState } from '@hopin-team/dashboard-shared-react';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import LoaderSkeleton from '../loader/loader';

const ListView = ({ data }: { data: WatchedVideo[] }) => {
  return <div>{data.map(video => VideoRow(video))}</div>;
};

const VideoRow = (video: WatchedVideo) => {
  return (
    <div className="flex justify-between items-center break-words border-t border-neutral-b4 min-h-14 first:border-t-0">
      <div className="flex gap-2 h-full">
        <img
          src={video.thumbnailUrl}
          alt={video.videoTitle}
          className="w-12 h-8 rounded object-cover self-center"
        />
        <div className="flex flex-col gap-1 md:max-w-[9rem] lg:max-w-[11rem] xl:max-w-[15rem]">
          <Tooltip tooltip={video.videoTitle} placement="bottom-center">
            <div className="text-sm font-medium truncate">
              {video.videoTitle}
            </div>
          </Tooltip>
          <div className="text-xs text-neutral-b2">
            {formatDuration(video.videoDuration)}
          </div>
        </div>
      </div>
      <div className="text-sm font-medium">{video.timesWatched}</div>
    </div>
  );
};

export const MostViewedHubVideos = () => {
  const { state, dispatch } = useAnalyticsContext();
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const orgId: string = useReadDashboardSharedState('org_id') || '';

  const config = useMemo(
    () => ({
      dateRange: state.dateRange,
      startDate: state.startDate,
      endDate: state.endDate,
      name: 'timesWatched',
      direction: 'desc',
      limit: 6,
    }),
    [state.dateRange, state.startDate, state.endDate],
  );

  const { data, isLoading, isError } = useFetch(
    config,
    `mostViewedHubVideos-${orgId}`,
    fetchMostViewedHubVideos,
    transformToCamelCase,
  );

  const { hasData, hasMoreData } = useMemo(
    () => ({
      hasData: data?.length > 0,
      hasMoreData: data?.length > 5,
    }),
    [data],
  );

  const onViewAll = useCallback(() => {
    track('ola_overview_most_viewed_hub_videos_view_all_click', {});
    dispatch({
      type: 'analytics tab updated',
      payload: { index: 4, tabName: 'videos' },
    });
    dispatch({
      type: 'update videos sort key and direction',
      payload: { name: 'times_watched', direction: 'desc' },
    });
    dispatch({
      type: 'update search text',
      payload: { searchText: '' },
    });
  }, [dispatch, track]);

  useEffect(() => {
    if (!isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading]);

  return (
    <Card
      heading={t('analytics.most-viewed-hub-videos')}
      tooltip={t('analytics.most-viewed-hub-videos-tooltip')}
      subHeading={t('analytics.most-viewed-hub-videos-content')}
      loading={isLoading}
      showFooter={hasData && hasMoreData && !isError}
      onViewAll={onViewAll}
    >
      <>
        {isError && !isLoading && <ErrorScreen showButton={false} />}
        {!isError && !isLoading && !hasData && isInitialLoad && <NoResults />}
        {!isError && !isLoading && !hasData && !isInitialLoad && (
          <NoFilterResults />
        )}
        {!isError && !isLoading && hasData && (
          <ListView data={data.slice(0, 5)} />
        )}
        {isLoading && <LoaderSkeleton hasData={hasData} />}
      </>
    </Card>
  );
};
